import { formatMoney, formatNumber } from '@/workspace/artifacts/money';
import { CREDIT_MICROS, type BillingAccount, type CodeCheck, type PlanOption } from './api';

/** Swedish VAT is shown for Swedish customers and for anyone whose country is not known yet. */
export type VatMode = 'se' | 'unknown' | 'reverse';
export const SWEDISH_VAT = 0.25;

export function vatMode(account: Pick<BillingAccount, 'taxCountry'> | null | undefined): VatMode {
  const country = account?.taxCountry ?? null;
  if (!country) return 'unknown';
  return country.toUpperCase() === 'SE' ? 'se' : 'reverse';
}

export const withVat = (amount: number, mode: VatMode) =>
  mode === 'reverse' ? amount : Math.round(amount * (1 + SWEDISH_VAT));

export const sortPlans = (plans: readonly PlanOption[]) =>
  [...plans].sort((a, b) => a.credits - b.credits);

/** The plan a company pays for now, or -1 without an active plan. */
export function currentPlanIndex(plans: readonly PlanOption[], account: BillingAccount): number {
  if (!account.planActive || !account.planId) return -1;
  return plans.findIndex((plan) => plan.id === account.planId);
}

/** Where the slider starts: the current plan, one step up when credits ran out, or a plan that covers recent use. */
export function initialPlanIndex(
  plans: readonly PlanOption[],
  account: BillingAccount,
  recentCredits: number | null,
  exhausted: boolean,
): number {
  const current = currentPlanIndex(plans, account);
  if (current >= 0) return exhausted ? Math.min(current + 1, plans.length - 1) : current;
  if (recentCredits == null || recentCredits <= 0) return 0;
  const covering = plans.findIndex((plan) => plan.credits >= recentCredits);
  return covering < 0 ? plans.length - 1 : covering;
}

/** One discount at a time: a freshly checked code, or the one the plan already has. */
export interface EffectiveDiscount {
  percentOff: number;
  /** Months a fresh code lasts; null for a code that does not end. */
  months: number | null;
  /** When the active discount ends; null when it does not end or is fresh. */
  endsAt: string | null;
  fresh: boolean;
  code: string | null;
}

export function effectiveDiscount(
  account: BillingAccount,
  fresh: CodeCheck | null,
): EffectiveDiscount | null {
  if (account.discount)
    return {
      percentOff: account.discount.percentOff,
      months: null,
      endsAt: account.discount.endsAt,
      fresh: false,
      code: account.discount.code,
    };
  if (fresh)
    return {
      percentOff: fresh.percentOff,
      months: fresh.months,
      endsAt: null,
      fresh: true,
      code: fresh.code,
    };
  return null;
}

export const discounted = (amount: number, discount: EffectiveDiscount | null) =>
  discount ? Math.round(amount * (1 - discount.percentOff / 100)) : amount;

export function addMonths(instant: Date, months: number): Date {
  const next = new Date(instant.getTime());
  next.setMonth(next.getMonth() + months);
  return next;
}

export const kr = (amount: number, locale: string) =>
  formatMoney(amount, locale, 'SEK', { full: true });

export const credits = (amount: number, locale: string) =>
  formatNumber(Math.floor(amount), locale, { maximumFractionDigits: 0 });

/** Whole credits a balance holds: what "you have" means everywhere, so it is floored, never rounded up. */
export const wholeBalance = (amount: number) => Math.max(0, Math.floor(amount + 1e-9));
export const balanceCredits = (micros: number) => wholeBalance(micros / CREDIT_MICROS);

/** A whole percentage in the language's own form: "20%", "20 %". */
export const percentText = (value: number, locale: string) =>
  formatNumber(value / 100, locale, { style: 'percent', maximumFractionDigits: 0 });

/** Stripe states where the last payment did not go through. */
export const PAYMENT_FAILED = new Set(['past_due', 'unpaid', 'incomplete']);

/**
 * Where the company's plan stands: active, its last payment failed, it ended,
 * or there never was one. A plan whose checkout never completed counts as none.
 */
export type PlanState = 'active' | 'payment_failed' | 'ended' | 'none';
export function planState(
  account: Pick<BillingAccount, 'planActive' | 'planId' | 'planStatus'>,
): PlanState {
  if (account.planActive) return 'active';
  if (!account.planId) return 'none';
  if (PAYMENT_FAILED.has(account.planStatus)) return 'payment_failed';
  if (account.planStatus === 'canceled') return 'ended';
  return 'none';
}

/** When an ended plan ended: its last period's end, once that is past. */
export function planEndedAt(account: Pick<BillingAccount, 'periodEnd'>, now = new Date()) {
  const end = account.periodEnd ? new Date(account.periodEnd) : null;
  return end && end.getTime() <= now.getTime() ? end.toISOString() : null;
}

/** Price per credit in kronor with öre, e.g. "12.50 kr". */
export const perCredit = (orePerCredit: number, locale: string) =>
  `${formatNumber(orePerCredit / 100, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;

/** Short slider labels: 160, 1.2k, 74.4k. */
export const tickLabel = (amount: number, locale: string) =>
  amount < 1000
    ? formatNumber(amount, locale)
    : formatNumber(amount, locale, { notation: 'compact', maximumFractionDigits: 1 });

export const shortDate = (instant: string | Date, locale: string) =>
  new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(new Date(instant));

export const clockTime = (instant: string | Date, locale: string) =>
  new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(instant));

/** The ticks that stay visible on a narrow screen. */
export const MAJOR_TICKS = new Set([0, 3, 6, 9]);
